import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ProductMediaThumb from "../Product/ProductMediaPreview";
import GatewaySelectionDialog from "../Common/PaymentGatewaySelectionDialog";
import {
  getPaymentPlatform,
  normalizeGateway,
  savePaymentMeta,
  startPaymentFromOrderResponse,
} from "../Common/PaymentGatewayMethods";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";
import { resetOfflineOrderState } from "../../redux/OfflineOrder/offlineOrderSlice";
import {
  createJewelleryOfflineOrderRequest,
  createJewelleryOrderRequest,
  resetJewelleryOfflineOrderState,
  resetJewelleryOrderState,
} from "../../redux/Jewellery/jewellerySlice";
import {
  addJewelleryToCartRequest,
  fetchJewelleryCartRequest,
} from "../../redux/Jewellery/jewelleryCartSlice";
import {
  asList,
  availableInventory,
  formatMoney,
  maxOrderQuantity,
  jewelleryPurchaseModes,
  payableAmount,
  priceBreakdown,
  splitMedia,
  stockLabel,
  typeName,
} from "./jewelleryUtils";

const DetailRow = ({ label, value, textColor }) => {
  if (!value) return null;
  return (
    <Stack direction="row" justifyContent="space-between" gap={2} py={0.6}>
      <Typography sx={{ color: textColor, fontWeight: 500 }}>{label}</Typography>
      <Typography sx={{ color: textColor, fontWeight: 700, textAlign: "right" }}>
        {value}
      </Typography>
    </Stack>
  );
};

export default function JewelleryDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const themeColors = useAppTheme();
  const { symbol } = useCurrency();
  const jewellery = state?.jewellery;
  const { placing, orderResponse, offlinePlacing, offlineOrder } = useSelector(
    (store) => store.jewellery || {},
  );
  const cartItems = useSelector((store) => store.jewelleryCart?.items || []);
  const cartCount = cartItems.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0,
  );
  const { appConfig } = useSelector((store) => store.dashboard || {});
  const paymentGateways = appConfig?.paymentGateways || [];
  const { online: canBuyOnline, offline: canBuyOffline } =
    jewelleryPurchaseModes(appConfig);
  const rows = useMemo(() => availableInventory(jewellery), [jewellery]);
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");
  const [openGatewayDialog, setOpenGatewayDialog] = useState(false);
  const [openBuyDialog, setOpenBuyDialog] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState("");
  const offlineBuyRequested = useRef(false);
  const selected = rows[0] || null;
  const maxQty = maxOrderQuantity(selected);
  const amount = jewellery ? payableAmount(jewellery, quantity) : 0;
  const price = priceBreakdown(jewellery, quantity);
  const media = splitMedia(jewellery?.files);

  useEffect(() => {
    dispatch(fetchJewelleryCartRequest());
    dispatch(resetJewelleryOrderState());
  }, [dispatch]);

  useEffect(() => {
    if (!orderResponse || !selectedGateway) return;

    const pay = async () => {
      const launched = await startPaymentFromOrderResponse(
        orderResponse,
        selectedGateway,
      );
      if (!launched) {
        dispatch(resetJewelleryOrderState());
      }
    };

    pay();
  }, [orderResponse, selectedGateway, dispatch]);

  useEffect(() => {
    dispatch(resetJewelleryOfflineOrderState());
  }, [dispatch]);

  useEffect(() => {
    if (!offlineBuyRequested.current || !offlineOrder?.orderId || !jewellery) return;
    offlineBuyRequested.current = false;
    const orderId = offlineOrder.orderId;
    dispatch(resetOfflineOrderState());
    dispatch(resetJewelleryOfflineOrderState());
    navigate("/artificialJewelleryOfflinePayment", {
      state: {
        flow: "jewellery",
        appConfig,
        offlineOrderData: {
          orderId,
          metalName: "Jewellery",
          jewelleryName: jewellery.name,
          quantity,
          finalAmount: amount,
          tax: Number(price.tax || 0),
          weight: 0,
        },
      },
    });
  }, [offlineOrder, jewellery, quantity, amount, price.tax, appConfig, dispatch, navigate]);

  if (!jewellery) {
    return (
      <Typography sx={{ p: 4, textAlign: "center" }}>
        No artificial jewellery selected
      </Typography>
    );
  }

  const addToCart = () => {
    if (!selected) {
      toast.error("This piece is out of stock");
      return;
    }
    const existing = cartItems.find((line) => line.inventoryId === String(selected._id));
    if (existing && existing.quantity + quantity > maxQty) {
      toast.info("Maximum quantity for this piece is already in your cart");
      return;
    }
    dispatch(
      addJewelleryToCartRequest({
        jewellery: jewellery._id,
        inventory: selected._id,
        quantity,
      }),
    );
  };

  const handleOfflineBuy = () => {
    if (!selected) {
      toast.error("This piece is out of stock");
      return;
    }
    setOpenBuyDialog(false);
    offlineBuyRequested.current = true;
    dispatch(
      createJewelleryOfflineOrderRequest({
        final_amount: String(amount),
        metalName: "Jewellery",
        orderType: "Purchase",
        jewellery: jewellery._id,
      }),
    );
  };

  const handleOrderOnline = () => {
    if (!selected) {
      toast.error("This piece is out of stock");
      return;
    }
    if (!paymentGateways.length) {
      toast.warn("No payment gateways configured!");
      return;
    }
    if (paymentGateways.length === 1) {
      handleGatewaySelect(paymentGateways[0]);
      return;
    }
    setOpenGatewayDialog(true);
  };

  const openBuyOptions = () => {
    if (canBuyOnline && !canBuyOffline) {
      handleOrderOnline();
      return;
    }
    if (canBuyOffline && !canBuyOnline) {
      handleOfflineBuy();
      return;
    }
    setOpenBuyDialog(true);
  };

  const handleGatewaySelect = (gateway) => {
    setOpenGatewayDialog(false);
    if (!gateway || !selected) return;

    const normalized = normalizeGateway(gateway);
    setSelectedGateway(normalized);
    savePaymentMeta(
      {
        grams: 0,
        metal: "Jewellery",
        metalName: "Jewellery",
        amount,
        totalAmount: amount,
        tax: Number(price.tax || 0),
        selectedGateway: normalized,
        orderType: "Purchase",
        buyType: "Online",
        planid: "",
        moneysip: false,
      },
      { replace: true },
    );
    dispatch(resetJewelleryOrderState());
    dispatch(
      createJewelleryOrderRequest({
        jewellery: jewellery._id,
        inventory: selected._id,
        quantity,
        amount,
        customernote: note.trim(),
        transactionMode: normalized,
        buyType: "Online",
        orderType: "Purchase",
        metalName: "Jewellery",
        weight: 0,
        platform: getPaymentPlatform(),
        x_api_version: "2022-09-01",
      }),
    );
  };

  const label = stockLabel(jewellery);

  const warrantyText = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (n === 0) return "No warranty";
  return `${n} month${n === 1 ? "" : "s"}`;
};

const returnExchangeText = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (n === 0) return "No return or exchange available";
  return `${n} day${n === 1 ? "" : "s"}`;
};

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: themeColors.scaffoldGradientColors,
        p: { xs: 2, md: 4 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 900,
          boxShadow: 5,
          p: { xs: 2, md: 3 },
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
          color: themeColors.text,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Button
            onClick={() => navigate(-1)}
            sx={{ textTransform: "none", color: themeColors.primary }}
          >
            Back
          </Button>
          <IconButton
            aria-label="Cart"
            onClick={() => navigate("/artificialJewelleryCart")}
            sx={{ color: themeColors.primary }}
          >
            <Badge badgeContent={cartCount} color="error">
              <ShoppingCartIcon />
            </Badge>
          </IconButton>
        </Stack>
        <Typography
          variant="overline"
          sx={{ display: "block", textAlign: "center", color: themeColors.primary }}
        >
          Artificial Jewellery
        </Typography>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="baseline"
          spacing={1}
          sx={{ mb: 2, flexWrap: "wrap" }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, textAlign: "center", color: themeColors.primary }}
          >
            {jewellery.name}
          </Typography>
          {Number(jewellery.discount) > 0 && (
            <Typography
              sx={{ color: "#3DDC84", fontWeight: 800, fontSize: 22, whiteSpace: "nowrap" }}
            >
              ↓ {Number(jewellery.discount)}%
            </Typography>
          )}
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems={{ md: "center" }}
        >
          <ProductMediaThumb
            urls={media.images}
            videos={media.videos}
            name={jewellery.name}
            width={220}
            height={220}
          />
          <Box flex={1}>
            {jewellery.title ? (
              <Typography variant="h6" fontWeight={700}>
                {jewellery.title}
              </Typography>
            ) : null}
            {jewellery.subtitle ? (
              <Typography sx={{ mt: 0.5 }}>{jewellery.subtitle}</Typography>
            ) : null}
            <Typography sx={{ mt: 1 }} variant="body2">
              {[typeName(jewellery), jewellery.subType, jewellery.collection]
                .filter(Boolean)
                .join(" • ")}
            </Typography>
            <Typography variant="h5" sx={{ mt: 1.5, fontWeight: 800 }}>
              {formatMoney(symbol, price.unitDiscounted)}
            </Typography>
            <Typography sx={{ textDecoration: "line-through", opacity: 0.7 }}>
              MRP {formatMoney(symbol, Number(jewellery.mrp) || 0)}
              {price.discountPercent > 0 ? `  •  ${price.discountPercent}% off` : ""}
            </Typography>
            {price.tax > 0 && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                GST extra. Tax will be added in the price breakdown below.
              </Typography>
            )}
            <Typography sx={{ mt: 0.5, fontWeight: 700 }}>{label}</Typography>
          </Box>
        </Stack>

        {jewellery.description ? (
          <Typography sx={{ mt: 3 }}>{jewellery.description}</Typography>
        ) : null}

        <Divider sx={{ my: 2 }} />
        <DetailRow label="Product code" value={jewellery.productCode} textColor={themeColors.text} />
        <DetailRow label="Gender" value={jewellery.gender} textColor={themeColors.text} />
        <DetailRow label="Design style" value={jewellery.designStyle} textColor={themeColors.text} />
        <DetailRow
          label="Occasion"
          value={asList(jewellery.occasion).join(", ")}
          textColor={themeColors.text}
        />
        <DetailRow
          label="Tags"
          value={asList(jewellery.tags).join(", ")}
          textColor={themeColors.text}
        />
        <DetailRow
          label="Warranty"
          value={warrantyText(jewellery.warrantyPeriod)}
          textColor={themeColors.text}
        />
        <DetailRow
          label="Return and exchange"
          value={returnExchangeText(jewellery.returnExchangePeriod)}
          textColor={themeColors.text}
        />
        <DetailRow
          label="Eligibility"
          value={jewellery.returnExchangeEligibility}
          textColor={themeColors.text}
        />
        <DetailRow label="Care" value={jewellery.careInstructions} textColor={themeColors.text} />

        {rows.length === 0 ? (
          <Typography sx={{ mt: 2 }}>This piece is out of stock.</Typography>
        ) : null}

        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 2 }}>
          <Typography fontWeight={700} sx={{ mr: 1 }}>
            Quantity
          </Typography>
          <IconButton
            aria-label="Decrease quantity"
            disabled={quantity <= 1}
            onClick={() => setQuantity((value) => Math.max(1, value - 1))}
            sx={{
              width: 36,
              height: 36,
              border: "1px solid rgba(255,255,255,0.75)",
              color: "#fff",
            }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Typography fontWeight={700} sx={{ minWidth: 24, textAlign: "center" }}>
            {quantity}
          </Typography>
          <IconButton
            aria-label="Increase quantity"
            disabled={quantity >= maxQty}
            onClick={() => setQuantity((value) => Math.min(maxQty, value + 1))}
            sx={{
              width: 36,
              height: 36,
              border: "1px solid rgba(255,255,255,0.75)",
              color: "#fff",
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Stack>

        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Note for the store"
          value={note}
          onChange={(event) => setNote(event.target.value.slice(0, 300))}
          sx={{ mt: 2 }}
        />

        <Typography sx={{ mt: 3, mb: 1, fontWeight: 700, color: themeColors.primary }}>
          Price details
        </Typography>
        <DetailRow label="MRP" value={formatMoney(symbol, price.mrp)} textColor={themeColors.text} />
        <DetailRow
          label="Discount on MRP"
          value={
            price.discountPercent > 0
              ? `− ${formatMoney(symbol, price.discountAmount)} (${price.discountPercent}%)`
              : formatMoney(symbol, 0)
          }
          textColor={themeColors.text}
        />
        <DetailRow
          label="Discounted price"
          value={formatMoney(symbol, price.discounted)}
          textColor={themeColors.text}
        />
        <DetailRow
          label="Tax"
          value={
            price.gstPercent > 0
              ? `${formatMoney(symbol, price.tax)} (GST ${price.gstPercent}%)`
              : formatMoney(symbol, 0)
          }
          textColor={themeColors.text}
        />
        <Divider sx={{ my: 1 }} />
        <DetailRow
          label="Payable amount"
          value={formatMoney(symbol, price.payable)}
          textColor={themeColors.text}
        />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="flex-end"
          alignItems={{ sm: "center" }}
          spacing={2}
          sx={{ mt: 3 }}
        >
          <Button
            variant="outlined"
            disabled={!selected}
            onClick={addToCart}
            sx={{
              textTransform: "none",
              borderRadius: 3,
              px: 4,
              py: 1.2,
              borderColor: themeColors.primary,
              color: themeColors.primary,
            }}
          >
            Add to cart
          </Button>
          <Button
            variant="contained"
            disabled={
              placing ||
              offlinePlacing ||
              !selected ||
              (!canBuyOnline && !canBuyOffline)
            }
            onClick={openBuyOptions}
            sx={{
              textTransform: "none",
              borderRadius: 3,
              px: 4,
              py: 1.2,
              background: themeColors.primary,
              color: themeColors.text,
            }}
          >
            {offlinePlacing
              ? "Creating order..."
              : placing
                ? "Opening payment..."
                : "Buy"}
          </Button>
        </Stack>
      </Card>
      <Dialog
        open={openBuyDialog}
        onClose={() => setOpenBuyDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            background: themeColors.bigContainer,
            color: themeColors.text,
            border: `1px solid ${themeColors.scaffold}`,
          },
        }}
      >
        <DialogTitle sx={{ color: themeColors.primary, textAlign: "center" }}>
          Buy artificial jewellery
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pb: 1 }}>
            {canBuyOnline && (
            <Button
              variant="contained"
              disabled={placing || offlinePlacing}
              onClick={() => {
                setOpenBuyDialog(false);
                handleOrderOnline();
              }}
              sx={{
                textTransform: "none",
                borderRadius: 3,
                py: 1.2,
                background: themeColors.primary,
                color: themeColors.text,
              }}
            >
              Online Buy
            </Button>
            )}
            {canBuyOffline && (
            <Button
              variant="contained"
              disabled={placing || offlinePlacing}
              onClick={handleOfflineBuy}
              sx={{
                textTransform: "none",
                borderRadius: 3,
                py: 1.2,
                background: themeColors.primary,
                color: themeColors.text,
              }}
            >
              Offline Buy
            </Button>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
      <GatewaySelectionDialog
        open={openGatewayDialog}
        onClose={handleGatewaySelect}
        gateways={paymentGateways}
      />
    </Box>
  );
}
